/**
 * The Home feed (D354, docs/plans/2026-10-home-and-microapps.md, part C): cards built from what
 * the app already knows, in one order. DOM-free, so the order with a person's useful / not
 * useful / pin / hide applied is tested without React; HomeFeed.tsx only draws.
 *
 * Every card says where the thing stands (`status`), who has it (`who`) and where it runs
 * (`where`). The order: what needs the person, then what is blocked on them, then what is
 * running, then what finished, then what merely changed. On a team or organisation project the
 * feed also carries the team's side (a teammate's run, work assigned to the person, a proposal
 * to a team they own, what the organisation pushed, a node that went quiet); a private project
 * gets none of it. No model is called to rank.
 */
import { limitWaitingWords } from '../protocol/agentState.js';
import { possessive } from '../wording/possessive.js';
import { inboxLane } from '../schemas/inboxLanes.js';
import { riskLevelOf, riskLevelSentence } from '../wording/riskWords.js';
import { isDone, isHidden } from './prefsModel.js';
import { isTeamHome, personName } from './teamModel.js';
/** The desktop views that have a phone equivalent, by the same name. */
const PHONE_VIEWS = {
    inbox: 'inbox',
    workspace: 'workspace',
    timeline: 'timeline',
    plans: 'plans',
    roadmap: 'roadmap',
    tasks: 'tasks',
    memory: 'memory',
    git: 'git',
    routines: 'routines',
    risks: 'risks',
};
/** The kinds whose card is about one thing the phone can open by id, and which view that id belongs to. */
const PHONE_ID_KINDS = {
    'needs-you': 'inbox',
    decision: 'inbox',
    'team-change': 'inbox',
    'org-push': 'inbox',
    plan: 'plans',
    pr: 'git',
    work: 'tasks',
    finished: 'timeline',
    'team-run': 'timeline',
};
/**
 * Where a card opens on a phone, from its desktop view: the same name where the phone has the view, an app by its id,
 * and nothing for a pane (`panel:…`), an action (`action:…`) or a conversation. `id` is the card's own thing (its record,
 * plan, pull request, task or run) for the kinds that are about one.
 */
export function phoneOpenFor(view, kind, id) {
    if (view.startsWith('app:')) {
        const app = view.slice('app:'.length);
        return app ? { view: 'apps', id: app } : undefined;
    }
    const phone = PHONE_VIEWS[view];
    if (!phone)
        return undefined;
    const own = kind ? PHONE_ID_KINDS[kind] === phone : false;
    // A wrap-up card is about a plan's conversation, not a plan the phone can open by id.
    return own && id && !id.startsWith('wrap-up:') ? { view: phone, id } : { view: phone };
}
/** A desktop open with its phone equivalent beside it, when there is one. */
export function withPhone(open, kind, id) {
    const phone = phoneOpenFor(open.view, kind, id);
    return phone ? { ...open, phone } : { ...open };
}
/** What a kind looks like, in one place. */
export const KIND_FACTS = {
    'needs-you': { tone: 'amber', tier: 0, kicker: 'Needs you', source: 'Inbox', view: 'inbox', open: 'Open in the Inbox' },
    plan: { tone: 'violet', tier: 0, kicker: 'Plan waiting', source: 'Plans', view: 'plans', open: 'Open the plan' },
    decision: { tone: 'blue', tier: 0, kicker: 'Decision', source: 'Inbox', view: 'inbox', open: 'Open the decision' },
    'team-task': { tone: 'green', tier: 1, kicker: 'Team board', source: 'Team board', view: 'tasks', open: 'Open the board' },
    'team-change': { tone: 'violet', tier: 1, kicker: 'Team proposal', source: 'Inbox', view: 'inbox', open: 'Review the proposal' },
    finished: { tone: 'blue', tier: 3, kicker: 'Team Work', source: 'Timeline', view: 'timeline', open: 'Open the Timeline' },
    'team-run': { tone: 'blue', tier: 3, kicker: 'Team', source: 'Timeline', view: 'timeline', open: 'Open the Timeline' },
    watching: { tone: 'rose', tier: 4, kicker: 'Watchlist', source: 'Watchlist', view: 'risks', open: 'Open the Watchlist' },
    memory: { tone: 'violet', tier: 4, kicker: 'Memory proposal', source: 'Memory', view: 'memory', open: 'Open Memory' },
    routine: { tone: 'amber', tier: 4, kicker: 'Routine', source: 'Routines', view: 'routines', open: 'Open Routines' },
    update: { tone: 'slate', tier: 4, kicker: 'Update', source: 'Updates', view: 'panel:updates', open: 'See the update' },
    away: { tone: 'teal', tier: 4, kicker: 'While you were away', source: 'Timeline', view: 'timeline', open: 'Open the Timeline' },
    'org-push': { tone: 'slate', tier: 4, kicker: 'From your organisation', source: 'Inbox', view: 'inbox', open: 'Open in the Inbox' },
    'team-node': { tone: 'slate', tier: 4, kicker: 'Node', source: 'Office', view: 'workspace', open: 'Open the Office' },
    working: { tone: 'blue', tier: 2, kicker: 'Working now', source: 'Team', view: 'workspace', open: 'Open the Office' },
    work: { tone: 'green', tier: 4, kicker: 'Project work', source: 'Roadmap', view: 'tasks', open: 'Open Tasks' },
    pr: { tone: 'rose', tier: 4, kicker: 'Pull request', source: 'Source Control', view: 'git', open: 'Open Source Control' },
    // `action:` is Home's own: the card opens a dialog on Home rather than another view.
    dispatch: { tone: 'blue', tier: 1, kicker: 'Ready for the team', source: 'Tasks', view: 'action:dispatch', open: 'Send to…' },
    // A microapp's card (D354 phase 3): its own tone, icon and name replace these; the app is opened by id (`app:<id>`).
    app: { tone: 'slate', tier: 4, kicker: 'Your app', source: 'Your app', view: 'apps', open: 'Open the app' },
    // Someone named the person in Team Work (people in Team Work, 0.89.20): it asks nothing, and opens the message.
    mention: { tone: 'blue', tier: 3, kicker: 'Mentioned you', source: 'Team Work', view: 'inbox', open: 'Open the message' },
};
/** The kinds only a team or organisation project has. */
export const TEAM_KINDS = ['team-run', 'team-task', 'team-change', 'org-push', 'team-node', 'working', 'dispatch'];
// ── Time words ────────────────────────────────────────────────────────
export function startOfDay(ms) {
    const d = new Date(ms);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}
export function endOfDayOf(ms) {
    const d = new Date(ms);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() - 1;
}
/** "2 h 56 m", "41 m", "under a minute". */
export function workedWords(ms) {
    const m = Math.round(ms / 60_000);
    if (m < 1)
        return 'under a minute';
    if (m < 60)
        return `${m} m`;
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest ? `${h} h ${rest} m` : `${h} h`;
}
/** "just now", "12 minutes ago", "3 hours ago", "yesterday", "5 days ago". */
export function freshness(at, now) {
    const s = Math.max(0, Math.round((now - at) / 1000));
    if (s < 60)
        return 'just now';
    const m = Math.round(s / 60);
    if (m < 60)
        return `${m} ${m === 1 ? 'minute' : 'minutes'} ago`;
    const h = Math.round(m / 60);
    if (h < 24)
        return `${h} ${h === 1 ? 'hour' : 'hours'} ago`;
    const days = Math.round((startOfDay(now) - startOfDay(at)) / 86_400_000);
    return days <= 1 ? 'yesterday' : `${days} days ago`;
}
/** "41 min", "2 h 5 m". */
function spanWords(ms) {
    const m = Math.max(0, Math.round(ms / 60_000));
    if (m < 1)
        return 'under a minute';
    if (m < 60)
        return `${m} min`;
    const h = Math.floor(m / 60);
    return m % 60 ? `${h} h ${m % 60} m` : `${h} h`;
}
/** The cap on a card's title (the wire schema's, `shared/schemas/home.ts`). */
export const TITLE_MAX = 300;
function make(kind, id, title, at, now, x) {
    const f = KIND_FACTS[kind];
    const tier = x.tier ?? f.tier;
    return {
        id: `${kind}:${id}`,
        kind,
        tone: f.tone,
        tier,
        kicker: x.kicker ?? f.kicker,
        title: title.length > TITLE_MAX ? `${title.slice(0, TITLE_MAX - 1).trimEnd()}…` : title,
        ...(x.summary ? { summary: x.summary } : {}),
        status: x.status,
        ...(x.who ? { who: x.who } : {}),
        ...(x.where ? { where: x.where } : {}),
        because: `Source: ${f.source} · ${freshness(at, now)}`,
        at,
        ...(x.on !== undefined ? { on: x.on } : {}),
        needsYou: tier <= 1,
        changeKey: x.changeKey,
        // A card that says where it opens on a phone keeps that (a Team Work notice: its Inbox record); the rest map by view.
        open: ((o) => (x.open?.phone ? { ...o, phone: x.open.phone } : withPhone(o, kind, id)))({ view: x.open?.view ?? f.view, label: x.open?.label ?? f.open }),
        ...(x.opens ? { opens: x.opens.map((o) => (o.phone ? o : withPhone(o))) } : {}),
        ...(x.ask ? { ask: x.ask } : {}),
        ...(x.plan ? { plan: x.plan } : {}),
    };
}
export const EMPTY_SOURCES = { home: 'private', records: [], plansWaiting: [], runs: [], updates: [] };
const ms = (iso) => Date.parse(iso) || 0;
/** The kind a hub record becomes, or undefined when the feed does not show it. Team kinds need a team home. */
export function recordKind(r, home = 'private') {
    const key = r.dedupeKey ?? '';
    if (r.state !== 'unread' && r.state !== 'read')
        return undefined;
    // The update has its own source (the Updates store knows more than the record does).
    if (key === 'update')
        return undefined;
    if (isTeamHome(home)) {
        if (key.startsWith('work-claim:') || (key.startsWith('task:') && key.includes(':for:')))
            return 'team-task';
        if (key.startsWith('team-proposal:'))
            return 'team-change';
        if (key.startsWith('order:') || key.startsWith('policy:') || key.startsWith('promotion:'))
            return 'org-push';
    }
    if (key.startsWith('memory-proposal:'))
        return 'memory';
    if (key.startsWith('risk-review:'))
        return 'watching';
    if (key.startsWith('routine:'))
        return 'routine';
    if (r.kind === 'mention')
        return 'mention';
    if (key.startsWith('decision:') || r.kind === 'question')
        return 'decision';
    if (inboxLane({ dedupeKey: r.dedupeKey, attention: r.attention, kind: r.kind, title: r.title }) === 'inbox' && r.attention === 'needs-you')
        return 'needs-you';
    return undefined;
}
/** Action ids the Inbox keeps in a small menu rather than a button (`approvalModel.ts` MENU_ACTION_IDS): not on the card either. */
const MENU_ACTION_IDS = new Set(['allow-session']);
/** Records about work landing on a branch: a merge held, or a conflict to settle. */
const MERGE_PREFIXES = ['landing:', 'integration-conflict:'];
/** The most in-place buttons a card carries. */
const ACTS_MAX = 3;
/** AIOE's answer to a Team Work question, run as the person (shared/schemas/conversations.ts `TEAMWORK_ANSWER_RPC`). */
export const TEAMWORK_ANSWER_RPC = 'aioe.teamwork.answer';
/** A way into a Team Work session (`OPEN_VIEW_ACTION`): the window's own navigation. */
const OPEN_VIEW_RPC = 'open';
/** The Team Work message a record opens on (its `open` action), as a view a card's Open goes to. */
export function teamworkOpenView(r) {
    const params = r.actions?.find((a) => a.rpc === OPEN_VIEW_RPC && a.params?.view === 'teamwork')?.params;
    const id = typeof params?.id === 'string' && params.id ? params.id : undefined;
    if (!id)
        return undefined;
    return `conversation:${id}${typeof params?.messageId === 'string' && params.messageId ? `#${params.messageId}` : ''}`;
}
const recordAct = (r, a, extra = {}) => ({ kind: 'record', label: extra.label ?? a.label, recordId: r.id, actionId: a.id, ...(extra.reason ? { reason: true } : {}) });
/**
 * What a hub record asks, answered on its card: the same actions the Inbox shows for it, never one a person should see in
 * full first. An approval at risk 4–5 (D270) keeps Open as the primary and says why.
 */
export function recordAsk(r) {
    const key = r.dedupeKey ?? '';
    const actions = r.actions ?? [];
    if (key.startsWith('approval:')) {
        const level = riskLevelOf(r.riskLevel);
        if (level && level >= 4)
            return { acts: [], openBecause: `${riskLevelSentence(level)}. Open it to see what it would do before you answer.` };
        const acts = actions.filter((a) => a.rpc === 'permissions.resolve' && !MENU_ACTION_IDS.has(a.id)).slice(0, ACTS_MAX).map((a) => recordAct(r, a));
        return acts.length ? { acts } : undefined;
    }
    // Work landing on a branch: what lands is read where it can be seen, never said yes to from a card.
    if (MERGE_PREFIXES.some((p) => key.startsWith(p)))
        return { acts: [], openBecause: 'A merge: open it to see what it merges before you say yes.' };
    // An answer among several leaves the record open (`keepOpen`): it opens something rather than answering. A way into
    // a Team Work session (`rpc: "open"`, 0.89.20) is the card's Open, never an answer.
    const answers = actions.filter((a) => a.params?.keepOpen !== true && a.rpc !== OPEN_VIEW_RPC);
    // A Team Work question (0.89.20): an option answers it in one press; one without a value is answered in words.
    if (r.kind === 'question' && answers.some((a) => a.rpc === TEAMWORK_ANSWER_RPC)) {
        const acts = answers.slice(0, ACTS_MAX + 1).map((a) => (a.rpc === TEAMWORK_ANSWER_RPC && typeof a.params?.value !== 'string' ? { ...recordAct(r, a, { label: 'Answer…' }), words: true } : recordAct(r, a)));
        return { acts };
    }
    if (key.startsWith('memory-proposal:')) {
        const accept = answers.find((a) => a.id === 'accept');
        const reject = answers.find((a) => a.id === 'reject');
        const acts = [...(accept ? [recordAct(r, accept)] : []), ...(reject ? [recordAct(r, reject, { reason: true })] : [])];
        return acts.length ? { acts } : undefined;
    }
    if (key.startsWith('app-review:') || key.startsWith('routine-review:')) {
        const approve = answers.find((a) => a.id === 'approve');
        // A rejection is given with its reason, on the review itself.
        return approve ? { acts: [recordAct(r, approve), { kind: 'open', label: 'Reject…' }] } : undefined;
    }
    if (key.startsWith('decision:') || r.kind === 'question') {
        const acts = answers.slice(0, ACTS_MAX + 1).map((a) => recordAct(r, a));
        return acts.length ? { acts } : { acts: [], openBecause: 'It asks for an answer in your own words: open it to reply.' };
    }
    return undefined;
}
/** A plan's name, for a group heading: "PLAN-3 · Add the login page", cut to fit. */
function planName(reference, objective) {
    const name = objective.trim() ? `${reference} · ${objective.trim()}` : reference;
    return name.length > 120 ? `${name.slice(0, 119).trimEnd()}…` : name;
}
/** A wrap-up waiting on the person: its step's button, or why it is opened instead. Nothing while only agents have it. */
function wrapUpAsk(w) {
    if (!w.waitingOnYou)
        return undefined;
    if (w.openBecause)
        return { acts: [], openBecause: w.openBecause };
    return w.step ? { acts: [{ kind: 'message', label: w.step.label, type: w.step.type, payload: w.step.payload }] } : undefined;
}
function clip(text, max = 220) {
    const t = text?.replace(/\s+/g, ' ').trim();
    if (!t)
        return undefined;
    return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}
const RECORD_STATUS = {
    'needs-you': 'Waiting on you',
    decision: 'Waiting on you · a decision',
    memory: 'Proposed · waiting for a yes',
    watching: 'Changed',
    routine: 'Ran',
    'team-task': 'Assigned to you',
    'team-change': 'Waiting on you · a proposal',
    'org-push': 'Pushed by your organisation',
    mention: 'Mentioned you',
};
/** "this machine" for work this workbench ran; otherwise the honest "elsewhere in the team". */
function placeOf(run) {
    if (run.here)
        return 'this machine';
    return undefined;
}
function runCard(run, now, kind) {
    const at = run.endedAt ?? run.startedAt;
    // A run that ended on a usage limit is waiting for it, never "stopped with an error".
    const limited = !run.live && !!run.limit;
    const failed = !limited && (run.outcome === 'failed' || run.outcome === 'error');
    // A run set going on this machine, or by the host's "You", is the reader's own: "Your Juno", never "You’s Juno".
    const who = run.person ? `${possessive(run.person, { self: run.here, start: true })} ${run.agent}` : run.agent;
    const asks = run.live && !!run.card?.needsYou;
    const verb = asks ? 'is waiting for you' : run.live ? 'is working' : limited ? 'is waiting for its usage limit' : failed ? 'stopped with an error' : 'finished';
    const status = run.live ? `${run.card?.state ?? 'Running'} · ${spanWords(now - run.startedAt)}` : limited ? limitWaitingWords(run.limit?.resetsAt, now) : failed ? 'Stopped with an error' : 'Finished';
    return make(kind, run.id, `${who} ${verb} in ${run.repository}`, at, now, {
        summary: run.live ? (run.card?.doing ? `${run.agent} is ${run.card.doing}.` : `${run.team} is part way through a run.`) : limited ? `${run.team} is restarted when the limit lifts, or its work can be handed to another account or runtime.` : `A run for ${run.team} is over. Its changes are in the Timeline.`,
        changeKey: `${run.id}|${run.live ? 'live' : (run.outcome ?? 'ended')}`,
        kicker: `${KIND_FACTS[kind].kicker} · ${run.live ? 'running now' : 'finished'}`,
        status,
        who,
        ...(placeOf(run) ? { where: placeOf(run) } : {}),
        // A run still going is running, whatever its kind says about finished work.
        tier: run.live || limited ? 2 : failed ? 1 : 3,
    });
}
/** Kinds whose records only inform: many with one title are one card. A card that asks something is never merged. */
const COLLAPSE_KINDS = ['org-push', 'routine', 'watching'];
/** One card per key: the newest, "×N" after its kicker and the count in its change key, the Inbox as its way in. */
export function collapseByTitle(cards) {
    const by = new Map();
    for (const c of cards)
        by.set(c.key, [...(by.get(c.key) ?? []), c.item]);
    const out = [];
    for (const items of by.values()) {
        const [newest, ...rest] = [...items].sort((a, b) => b.at - a.at || b.id.localeCompare(a.id));
        if (!newest)
            continue;
        out.push(rest.length === 0 ? newest : { ...newest, kicker: `${newest.kicker} · ×${items.length}`, changeKey: `${newest.changeKey}|×${items.length}` });
    }
    return out;
}
/** Every card the sources offer, unordered. */
export function feedItems(src, now) {
    const out = [];
    const team = isTeamHome(src.home);
    const people = src.team?.people ?? {};
    // Records that only inform (the organisation's pushes, a routine's runs) and share a title are one card, newest first.
    const grouped = [];
    for (const r of src.records) {
        const kind = recordKind(r, src.home);
        if (!kind)
            continue;
        const at = ms(r.at);
        // A routine is news: only while nobody has looked at it.
        if (kind === 'routine' && r.state !== 'unread')
            continue;
        const actor = r.by?.actor;
        const principal = r.by?.principal && r.by.principal !== actor ? personName(r.by.principal, people) : undefined;
        const who = kind === 'team-task' || kind === 'team-change' || kind === 'org-push' ? (principal ?? actor) : (actor ?? r.teamName);
        const ask = recordAsk(r);
        // A mention or a Team Work question opens its session at the message (0.89.20); a phone, which has no Team Work, its Inbox record.
        const into = teamworkOpenView(r);
        const card = make(kind, r.id, r.title, at, now, { summary: clip(r.body), changeKey: `${r.at}|${r.state}`, status: RECORD_STATUS[kind] ?? 'Changed', ...(who ? { who } : {}), ...(ask ? { ask } : {}), ...(into ? { open: { view: into, label: kind === 'mention' ? 'Open the message' : 'Open the question', phone: { view: 'inbox', id: r.id } } } : {}) });
        if (COLLAPSE_KINDS.includes(kind))
            grouped.push({ item: card, key: `${kind}|${r.title}` });
        else
            out.push(card);
    }
    out.push(...collapseByTitle(grouped));
    for (const p of src.plansWaiting) {
        out.push(make('plan', p.key, `${p.reference}: ${p.objective}`, p.updatedAt ?? now, now, {
            summary: `${p.steps} ${p.steps === 1 ? 'step' : 'steps'} for ${p.team}, waiting for your approval before anyone starts. ${p.stamp}`.trim(),
            changeKey: `${p.key}|waiting|${p.steps}`,
            kicker: 'Plan · waiting for your approval',
            status: 'Waiting on you · approval',
            who: p.team,
            plan: { id: p.planId ?? p.key, name: planName(p.reference, p.objective) },
        }));
    }
    for (const w of src.wrapUps ?? []) {
        out.push(make('plan', `wrap-up:${w.key}`, `${w.reference}: ${w.objective}`, w.updatedAt ?? now, now, {
            summary: w.header,
            changeKey: `${w.key}|wrap-up|${w.header}`,
            kicker: 'Plan · wrapping up',
            status: w.waitingOnYou ? 'Waiting on you · wrap-up' : 'Wrap-up · in progress',
            who: w.team,
            plan: { id: w.planId ?? w.key, name: planName(w.reference, w.objective) },
            open: { view: w.conversationId ? `conversation:${w.conversationId}` : 'plans', label: 'Open the wrap-up' },
            ...(wrapUpAsk(w) ? { ask: wrapUpAsk(w) } : {}),
        }));
    }
    for (const run of src.runs)
        out.push(runCard(run, now, 'finished'));
    if (team)
        for (const run of src.team?.runs ?? [])
            out.push(runCard(run, now, 'team-run'));
    if (team) {
        for (const n of src.team?.nodes ?? []) {
            if (!n.unreachable)
                continue;
            out.push(make('team-node', n.name, `The node ${n.name} went quiet`, now, now, {
                summary: 'Agents placed on it cannot start until it is back. Nothing it was running is lost.',
                changeKey: `${n.name}|quiet`,
                status: 'Not reachable',
                where: n.name,
            }));
        }
    }
    for (const u of src.updates) {
        out.push(make('update', u.id, u.state === 'ready' ? `${u.title} is ready to install` : `${u.title} has a new version`, now, now, {
            summary: u.latest ? `Version ${u.latest}. Nothing installs until you say so.` : 'Nothing installs until you say so.',
            changeKey: `${u.id}|${u.latest ?? ''}|${u.state}`,
            status: u.state === 'ready' ? 'Ready to install' : 'Available',
            where: 'this machine',
            ...(u.state === 'ready' && u.install ? { ask: { acts: [{ kind: 'install', label: u.install }] } } : {}),
        }));
    }
    if (team) {
        for (const w of src.working ?? []) {
            out.push(make('working', w.id, `${w.who}: ${w.what}`, now, now, {
                changeKey: `${w.id}|${w.what}`,
                status: w.since ? `Working · ${w.since}` : 'Working',
                who: w.who,
                ...(w.where ? { where: w.where } : {}),
            }));
        }
        if (src.dispatch && src.dispatch.ready > 0) {
            const d = src.dispatch;
            out.push(make('dispatch', 'ready', `${d.ready} ${d.ready === 1 ? 'task is' : 'tasks are'} ready to send to the team`, now, now, {
                summary: clip(d.titles.slice(0, 3).join(' · ')),
                changeKey: `ready|${d.ready}`,
                status: 'Waiting on you · to send',
            }));
        }
    }
    for (const w of src.work ?? []) {
        out.push(make('work', w.id, w.roadmap ? w.title : `${w.id} ${w.title}`, now, now, {
            ...(w.summary ? { summary: clip(w.summary) } : {}),
            changeKey: `${w.id}|${w.status}`,
            status: w.status,
            tier: w.blocked ? 1 : 4,
            open: w.roadmap ? { view: 'roadmap', label: 'Open the Roadmap' } : {},
            kicker: w.roadmap ? 'Project roadmap' : w.blocked ? 'Project task · blocked' : 'Project task',
        }));
    }
    for (const p of (src.pullRequests ?? []).slice(0, 12)) {
        out.push(make('pr', p.id, `${p.ref} ${p.title}`, now, now, {
            changeKey: `${p.id}|${p.group}`,
            status: p.group === 'review' ? 'Waiting on your review' : p.group === 'mine' ? 'Open · yours' : 'Open · the team’s',
            who: p.by,
            ...(p.several ? { where: p.repository } : {}),
            tier: p.group === 'review' ? 0 : 4,
            kicker: p.group === 'review' ? 'Pull request · to review' : 'Pull request',
        }));
    }
    if (src.away && src.away.runs > 0) {
        const a = src.away;
        out.push(make('away', 'timeline', `${a.runs} ${a.runs === 1 ? 'run' : 'runs'} finished while you were away`, a.at, now, {
            summary: `${a.agents} ${a.agents === 1 ? 'agent' : 'agents'} worked for ${a.worked}. ${a.parts.map((p) => p.label).join(' · ')}`.trim(),
            changeKey: `away|${a.runs}`,
            status: 'Since you last looked',
            opens: a.parts.map((p) => ({ view: p.view, label: p.label })),
        }));
    }
    return out;
}
// ── Order ─────────────────────────────────────────────────────────────
/** One step of a kind's weight is worth this much recency: a kind marked useful rises past what is a few hours newer. */
export const WEIGHT_MS = 6 * 60 * 60 * 1000;
/**
 * The feed a person sees: hidden cards removed, a pinned card first (the first pin leads), then
 * by tier — needs the person, blocked on them, running, finished, changed — and inside a tier
 * the newest first, moved by the weights they taught (a weight never moves a card across a
 * tier). With a day pressed the feed narrows to that day's cards, and **cards that wait on the
 * person never filter**: they stay whichever day it is.
 */
export function buildFeed(items, prefs, now, filter) {
    const seen = new Set();
    const kept = items.filter((i) => {
        if (seen.has(i.id))
            return false;
        seen.add(i.id);
        // A card that asks something is answered, never done: a Done kept for it (from a surface that could not answer it) is ignored.
        if (isHidden(prefs, i.id, i.changeKey, now) || (!asksSomething(i) && isDone(prefs, i)))
            return false;
        if (!filter || i.needsYou)
            return true;
        // Undated cards are about now: they belong to today.
        return (i.on ?? filter.today) === filter.day;
    });
    const score = (i) => i.at + (prefs.weights[i.kind] ?? 0) * WEIGHT_MS;
    const pinOf = (i) => {
        const at = prefs.pinned.indexOf(i.id);
        return at < 0 ? Infinity : at;
    };
    return kept.sort((a, b) => {
        const pa = pinOf(a);
        const pb = pinOf(b);
        if (pa !== pb)
            return pa - pb;
        if (a.tier !== b.tier)
            return a.tier - b.tier;
        return score(b) - score(a) || a.id.localeCompare(b.id);
    });
}
/** The card waits on the person or asks them something: it is answered, never done or cleared. */
export function asksSomething(item) {
    return item.needsYou || !!item.ask;
}
/** The meta line on a card: "Running · 41 min · Generalist · this machine". */
export function metaLine(item) {
    // A node is both who has it and where it runs: its name once.
    const where = item.where && item.where.trim().toLowerCase() === item.who?.trim().toLowerCase() ? undefined : item.where;
    const parts = [];
    for (const part of [item.status, item.who, where])
        if (part && !parts.some((p) => p.toLowerCase() === part.toLowerCase()))
            parts.push(part);
    return parts.join(' · ');
}
/** The most cards the carousel holds; the rest are on the board under it. */
export const FEED_SHOWN = 20;
/** The carousel's cards (pins first, the order rule already applied) and how many there are in all. */
export function capFeed(feed, limit = FEED_SHOWN) {
    return { shown: feed.slice(0, limit), all: feed.length };
}
/** "01 / 20 · 95 in all": the place in the carousel, then the whole when the carousel holds fewer. */
export function counterWords(index, shown, all) {
    return all > shown ? `${counter(index, shown)} · ${all} in all` : counter(index, shown);
}
/** "01 / 09". */
export function counter(index, total) {
    const width = Math.max(2, String(total).length);
    return `${String(total ? index + 1 : 0).padStart(width, '0')} / ${String(total).padStart(width, '0')}`;
}
/** The sentence a screen reader gets for a card: "3 of 9". */
export function positionWords(index, total) {
    return `${index + 1} of ${total}`;
}
//# sourceMappingURL=feedModel.js.map